import { isEqual } from 'es-toolkit'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { Button, Card, CardBody, CardText, CardTitle, Col, FormGroup, Input, Label, Row } from 'reactstrap'

import type { DefaultShift, DefaultShiftSchedule } from 'api/workers/types'

import { showError, showSuccess } from 'slices/notificationSlice'
import { getTemplateList, selectTemplateStatus } from 'slices/templateSlice'
import { selectWorkersStatus, updateWorker } from 'slices/workersSlice'

import { CardSubmitFooter, CustomButton, NavMenu, SelectBoxFormat } from 'components/common'
import * as Rules from 'components/common/FormFormat/ValidationRules'

import useBusinessTime from 'hooks/useBusinessTime'
import { useWorkerListPanel } from 'hooks/useWorkerListPanel'

import placeholder from 'images/allEmpty.svg'

import { WorkerListPanel } from './WorkerListPanel'

import styles from './WorkerList.module.scss'

const DAYS_OF_WEEK = ['月曜', '火曜', '水曜', '木曜', '金曜', '土曜', '日曜'] as const
const MAX_SHIFTS_PER_DAY = 3
const ONE_WEEK_DAYS = 7
const BLANK_TEMPLATE = '未設定'

type EditDefaultShift = {
  active: boolean
  schedules: DefaultShiftSchedule[]
}

const createEmptySchedule = (): DefaultShiftSchedule => ({
  startTime: '',
  endTime: '',
  templateId: null,
})

const createInitialDefaultShifts = (): EditDefaultShift[] =>
  Array.from({ length: ONE_WEEK_DAYS }, () => ({
    active: false,
    schedules: [],
  }))

// 時間を分に変換
const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 60 + minutes
}

// 営業時間内かチェック
const isTimeWithinBusinessHours = (time: string, businessStartTime: string, businessEndTime: string): boolean => {
  if (!time) return true
  const timeMinutes = timeToMinutes(time)
  const startMinutes = timeToMinutes(businessStartTime)
  const endMinutes = timeToMinutes(businessEndTime)
  return timeMinutes >= startMinutes && timeMinutes <= endMinutes
}

// シフト開始時間のバリデーション（営業時間内チェック）
const validateScheduleStartTime = (
  schedule: DefaultShiftSchedule,
  businessStartTime: string,
  businessEndTime: string
): string | null => {
  if (schedule.startTime && !isTimeWithinBusinessHours(schedule.startTime, businessStartTime, businessEndTime)) {
    return '勤務開始時間は営業時間内にしてください'
  }
  return null
}

// シフト終了時間のバリデーション（終了時間 > 開始時間チェック & 営業時間内チェック）
const validateScheduleEndTime = (
  schedule: DefaultShiftSchedule,
  businessStartTime: string,
  businessEndTime: string
): string | null => {
  // 営業時間内チェック
  if (schedule.endTime && !isTimeWithinBusinessHours(schedule.endTime, businessStartTime, businessEndTime)) {
    return '勤務終了時間は営業時間内にしてください'
  }
  // 終了時間 > 開始時間チェック
  if (schedule.startTime && schedule.endTime) {
    const startMinutes = timeToMinutes(schedule.startTime)
    const endMinutes = timeToMinutes(schedule.endTime)
    if (endMinutes <= startMinutes) {
      return '勤務終了時間は勤務開始時間の後にしてください'
    }
  }
  return null
}

// 単一スケジュールにバリデーションエラーがあるかどうか
const hasScheduleError = (
  schedule: DefaultShiftSchedule,
  businessStartTime: string,
  businessEndTime: string,
  templateIds: number[]
): boolean => {
  // 必須チェック
  if (!schedule.startTime || !schedule.endTime) {
    return true
  }
  // 営業時間内チェック & 終了時間 > 開始時間チェック & テンプレートチェック
  return (
    validateScheduleStartTime(schedule, businessStartTime, businessEndTime) !== null ||
    validateScheduleEndTime(schedule, businessStartTime, businessEndTime) !== null ||
    validateScheduleTemplate(schedule, templateIds) !== null
  )
}

// スケジュールにバリデーションエラーがあるかどうか
const hasScheduleValidationErrors = (
  schedules: DefaultShiftSchedule[],
  businessStartTime: string,
  businessEndTime: string,
  templateIds: number[]
): boolean => {
  return schedules.some(schedule => hasScheduleError(schedule, businessStartTime, businessEndTime, templateIds))
}

// シフト時間の重複チェック
const hasOverlappingSchedules = (schedules: DefaultShiftSchedule[]): boolean => {
  if (schedules.length < 2) return false

  const sortedSchedules = [...schedules].sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime))

  return sortedSchedules.some((schedule, i) => {
    if (i === sortedSchedules.length - 1) return false
    const currentEnd = timeToMinutes(schedule.endTime)
    const nextStart = timeToMinutes(sortedSchedules[i + 1].startTime)
    return currentEnd > nextStart
  })
}

// テンプレートのバリデーション（対象ワークスペースのテンプレートかチェック）
const validateScheduleTemplate = (schedule: DefaultShiftSchedule, templateIds: number[]): string | null => {
  // テンプレートが未選択の場合はエラーなし
  if (!schedule.templateId) {
    return null
  }
  // テンプレートが現在のワークスペースに存在するかチェック
  if (!templateIds.includes(schedule.templateId)) {
    return '対象外の作業予定テンプレートです'
  }
  return null
}

export const DefaultShifts = () => {
  const {
    worker,
    listItems,
    hasListItems,
    filterItems,
    filterWord,
    filterParams,
    disabledSearchButton,
    handleSearchInput,
    handleFilterChange,
    handleGetWorkerList,
    clearFilterWord,
    handleWorkerIdChange,
    handleScroll,
    WORKER_LIST_LIMIT,
  } = useWorkerListPanel({ regularMemberOnly: true, showDefaultShiftError: true })

  const dispatch = useDispatch()
  const { templateList } = useSelector(selectTemplateStatus, shallowEqual)
  const { isRequesting, errorMessage } = useSelector(selectWorkersStatus, shallowEqual)

  // useBusinessTime hookで営業時間を取得
  const { businessStartTime, businessEndTime } = useBusinessTime()

  // 時間選択肢を生成（営業時間内、15分間隔）
  const baseTimeOptions = useMemo(() => {
    const [startHour, startMinute] = businessStartTime.split(':').map(Number)
    const [endHour, endMinute] = businessEndTime.split(':').map(Number)
    const hours = Array.from({ length: endHour - startHour + 1 }, (_, i) => startHour + i)
    const minutes = [0, 15, 30, 45]

    return hours.flatMap(hour =>
      minutes
        .filter(minute => {
          // 開始時間より前はスキップ
          if (hour === startHour && minute < startMinute) return false
          // 終了時間より後はスキップ
          if (hour === endHour && minute > endMinute) return false
          return true
        })
        .map(minute => {
          const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
          return { key: timeStr, value: timeStr }
        })
    )
  }, [businessStartTime, businessEndTime])

  const [editDefaultShifts, setEditDefaultShifts] = useState<EditDefaultShift[]>(createInitialDefaultShifts())
  const [initDefaultShifts, setInitDefaultShifts] = useState<EditDefaultShift[]>(createInitialDefaultShifts())
  const [submitted, setSubmitted] = useState(false)

  // ワーカーのワークスペースIDを取得
  const workerWorkspaceId = useMemo(() => worker?.workspaceId, [worker?.workspaceId])

  // ワークスペースが変わったらテンプレートリストを取得
  useEffect(() => {
    if (workerWorkspaceId) {
      dispatch(getTemplateList(workerWorkspaceId))
    }
  }, [dispatch, workerWorkspaceId])

  // ワーカーが変わったらデフォルトシフトを初期化
  useEffect(() => {
    if (worker?.defaultShifts) {
      const shifts = worker.defaultShifts.map(shift => ({
        active: shift.active,
        schedules: shift.schedules.map(s => ({ ...s })),
      }))
      setEditDefaultShifts(shifts)
      setInitDefaultShifts(shifts)
      return
    }
    const initial = createInitialDefaultShifts()
    setEditDefaultShifts(initial)
    setInitDefaultShifts(initial)
  }, [worker])

  // テンプレート選択肢
  const templateOptions = useMemo(() => templateList.map(t => ({ key: t.id, value: t.name })), [templateList])

  // テンプレートIDリスト（バリデーション用）
  const templateIds = useMemo(() => templateList.map(t => t.id), [templateList])

  // 開始時間の表示値を取得（営業時間外の場合はその値を表示）
  const getStartTimeDisplayValue = useCallback(
    (startTime: string) => (startTime && !baseTimeOptions.some(opt => opt.key === startTime) ? startTime : undefined),
    [baseTimeOptions]
  )

  // 終了時間の表示値を取得（営業時間外または開始時間以前の場合はその値を表示）
  const getEndTimeDisplayValue = useCallback(
    (startTime: string, endTime: string) => {
      if (!endTime) return undefined
      const isOutsideBusinessHours = !baseTimeOptions.some(opt => opt.key === endTime)
      const isBeforeOrEqualStart = startTime && timeToMinutes(endTime) <= timeToMinutes(startTime)
      return isOutsideBusinessHours || isBeforeOrEqualStart ? endTime : undefined
    },
    [baseTimeOptions]
  )

  // 終了時間の選択肢を取得（開始時間より後の時間のみ）
  const getEndTimeOptions = useCallback(
    (startTime: string) => {
      if (startTime) {
        return baseTimeOptions.filter(opt => timeToMinutes(opt.key) > timeToMinutes(startTime))
      }
      return baseTimeOptions
    },
    [baseTimeOptions]
  )

  // テンプレートの表示値を取得
  const getTemplateDisplayValue = useCallback(
    (templateId: number | null) => {
      if (templateId) {
        return templateIds.includes(templateId) ? undefined : '対象外の作業予定テンプレート'
      }
      return BLANK_TEMPLATE
    },
    [templateIds]
  )

  // テンプレートの選択肢
  const templateSelectOptions = useMemo(() => [{ value: BLANK_TEMPLATE }, ...templateOptions], [templateOptions])

  // 曜日の有効/無効を切り替え
  const handleDayToggle = useCallback(
    (dayIndex: number) => {
      setEditDefaultShifts(prev => {
        const newShifts = [...prev]
        const isActive = !newShifts[dayIndex].active
        const currentSchedules = newShifts[dayIndex].schedules

        if (isActive) {
          // アクティブにする時、スケジュールがなければ空のスケジュールを追加
          newShifts[dayIndex] = {
            active: true,
            schedules: currentSchedules.length === 0 ? [createEmptySchedule()] : currentSchedules,
          }
        } else {
          // 非アクティブにする時、エラーのあるスケジュールを削除
          const validSchedules = currentSchedules.filter(
            schedule => !hasScheduleError(schedule, businessStartTime, businessEndTime, templateIds)
          )
          newShifts[dayIndex] = {
            active: false,
            schedules: validSchedules,
          }
        }
        return newShifts
      })
    },
    [businessStartTime, businessEndTime, templateIds]
  )

  // シフト時間を更新
  const handleScheduleChange = useCallback(
    (dayIndex: number, scheduleIndex: number, field: 'startTime' | 'endTime', time: string) => {
      setEditDefaultShifts(prev =>
        prev.map((day, dIdx) =>
          dIdx === dayIndex
            ? {
                ...day,
                schedules: day.schedules.map((sched, sIdx) =>
                  sIdx === scheduleIndex ? { ...sched, [field]: time } : sched
                ),
              }
            : day
        )
      )
    },
    []
  )

  // テンプレートを更新
  const handleTemplateChange = useCallback((dayIndex: number, scheduleIndex: number, templateId: number | null) => {
    setEditDefaultShifts(prev =>
      prev.map((day, dIdx) =>
        dIdx === dayIndex
          ? {
              ...day,
              schedules: day.schedules.map((sched, sIdx) =>
                sIdx === scheduleIndex ? { ...sched, templateId } : sched
              ),
            }
          : day
      )
    )
  }, [])

  // シフトを追加
  const handleAddSchedule = useCallback((dayIndex: number) => {
    setEditDefaultShifts(prev =>
      prev.map((day, dIdx) =>
        dIdx === dayIndex ? { ...day, schedules: [...day.schedules, createEmptySchedule()] } : day
      )
    )
  }, [])

  // シフトを削除
  const handleRemoveSchedule = useCallback((dayIndex: number, scheduleIndex: number) => {
    setEditDefaultShifts(prev =>
      prev.map((day, dIdx) => {
        if (dIdx !== dayIndex) return day
        const newSchedules = day.schedules.filter((_, i) => i !== scheduleIndex)
        return { ...day, schedules: newSchedules, active: newSchedules.length > 0 }
      })
    )
  }, [])

  // 変更があるかどうか
  const unchanged = useMemo(() => isEqual(editDefaultShifts, initDefaultShifts), [editDefaultShifts, initDefaultShifts])

  // 重複エラーがある曜日を取得
  const daysWithOverlap = useMemo(
    () =>
      editDefaultShifts
        .map((shift, index) => (shift.active && hasOverlappingSchedules(shift.schedules) ? index : -1))
        .filter(index => index !== -1),
    [editDefaultShifts]
  )

  // バリデーションエラーがある曜日を取得
  const daysWithValidationErrors = useMemo(
    () =>
      editDefaultShifts
        .map((shift, index) =>
          shift.active && hasScheduleValidationErrors(shift.schedules, businessStartTime, businessEndTime, templateIds)
            ? index
            : -1
        )
        .filter(index => index !== -1),
    [editDefaultShifts, businessStartTime, businessEndTime, templateIds]
  )

  // 送信可能かどうか
  const canSubmit = useMemo(
    () => !unchanged && daysWithOverlap.length === 0 && daysWithValidationErrors.length === 0,
    [unchanged, daysWithOverlap, daysWithValidationErrors]
  )

  // キャンセル
  const handleCancel = useCallback(() => {
    setEditDefaultShifts(
      initDefaultShifts.map(shift => ({
        active: shift.active,
        schedules: shift.schedules.map(s => ({ ...s })),
      }))
    )
  }, [initDefaultShifts])

  // 送信後のレスポンス処理
  useEffect(() => {
    if (!submitted || isRequesting) {
      return
    }
    if (errorMessage === '') {
      dispatch(showSuccess())
    } else {
      dispatch(showError())
    }
    setSubmitted(false)
  }, [submitted, isRequesting, errorMessage, dispatch])

  // 送信
  const handleSubmit = useCallback(() => {
    if (!worker) {
      return
    }

    const defaultShifts: DefaultShift[] = editDefaultShifts.map(shift => ({
      active: shift.active,
      schedules: shift.schedules.map(s => ({
        startTime: s.startTime,
        endTime: s.endTime,
        templateId: s.templateId ?? null,
      })),
    }))

    const workerListParams = { limit: WORKER_LIST_LIMIT, includeSpotWorker: false, ...filterParams }
    setSubmitted(true)
    dispatch(updateWorker(worker.id, { defaultShifts }, workerListParams))
  }, [dispatch, worker, editDefaultShifts, filterParams, WORKER_LIST_LIMIT])

  return (
    <NavMenu>
      <div className="mt-3 mx-3">
        <div className="d-flex justify-content-between mb-3">
          <div className="font-x-large fw-bold align-self-center">デフォルトシフト管理</div>
        </div>
        <Row className={styles.row}>
          <WorkerListPanel
            listItems={listItems}
            selectedId={worker?.id}
            filterItems={filterItems}
            filterWord={filterWord}
            disabledSearchButton={disabledSearchButton}
            onSearchInput={handleSearchInput}
            onFilterChange={handleFilterChange}
            onSearch={handleGetWorkerList}
            onClearFilter={clearFilterWord}
            onSelectWorker={handleWorkerIdChange}
            onScroll={handleScroll}
          />
          <Col md={8} className="h-100">
            <Card className="h-100">
              {hasListItems && worker ? (
                <>
                  <div className={styles.memberDetail}>
                    <CardBody>
                      <CardTitle className="font-large fw-bold">デフォルトシフト設定</CardTitle>
                      <CardText className="text-secondary">※必須項目</CardText>
                      <CardText>
                        メンバーそれぞれのデフォルトシフトを設定する事ができます。曜日毎に勤務時間とデフォルト作業予定テンプレートを選択できます。シフト管理で「自動作成」を行うと、デフォルトシフトを元にシフトは自動作成されます。
                      </CardText>

                      {DAYS_OF_WEEK.map((dayName, dayIndex) => {
                        // デフォルトシフトの配列は0:日曜〜6:土曜の順なので、曜日インデックスを変換
                        const convertedDayIndex = (dayIndex + 1) % ONE_WEEK_DAYS
                        const dayShift = editDefaultShifts[convertedDayIndex]
                        const hasOverlap = daysWithOverlap.includes(convertedDayIndex)

                        return (
                          <div key={dayName} className="mb-3 border rounded">
                            <div className="p-3">
                              <FormGroup check className="mb-0 d-flex align-items-center">
                                <Input
                                  type="checkbox"
                                  id={`day-${convertedDayIndex}`}
                                  checked={dayShift?.active ?? false}
                                  onChange={() => handleDayToggle(convertedDayIndex)}
                                  className="me-2"
                                />
                                <Label check for={`day-${convertedDayIndex}`} className="fw-bold mb-0">
                                  {dayName}
                                </Label>
                                {hasOverlap && dayShift?.active && (
                                  <span className="text-danger ms-3">勤務時間に重複があります</span>
                                )}
                              </FormGroup>
                            </div>

                            {dayShift.schedules.length > 0 && (
                              <div className="border-top">
                                {dayShift.schedules.map((schedule, scheduleIndex) => {
                                  const isLast = scheduleIndex === dayShift.schedules.length - 1
                                  return (
                                    <div key={scheduleIndex} className={scheduleIndex > 0 ? 'border-top' : ''}>
                                      <div className="p-3">
                                        <Row>
                                          <Col md={9}>
                                            <SelectBoxFormat
                                              className="mb-2"
                                              label="シフト開始時間※"
                                              placeholder="勤務開始時間を選択"
                                              value={schedule.startTime}
                                              displayValue={getStartTimeDisplayValue(schedule.startTime)}
                                              items={baseTimeOptions}
                                              onChange={item =>
                                                handleScheduleChange(
                                                  convertedDayIndex,
                                                  scheduleIndex,
                                                  'startTime',
                                                  item.key?.toString() || ''
                                                )
                                              }
                                              validations={[
                                                Rules.Required,
                                                () =>
                                                  validateScheduleStartTime(
                                                    schedule,
                                                    businessStartTime,
                                                    businessEndTime
                                                  ) || '',
                                              ]}
                                            />
                                            <SelectBoxFormat
                                              className="mb-2"
                                              label="シフト終了時間※"
                                              placeholder="勤務終了時間を選択"
                                              value={schedule.endTime}
                                              displayValue={getEndTimeDisplayValue(
                                                schedule.startTime,
                                                schedule.endTime
                                              )}
                                              items={getEndTimeOptions(schedule.startTime)}
                                              onChange={item =>
                                                handleScheduleChange(
                                                  convertedDayIndex,
                                                  scheduleIndex,
                                                  'endTime',
                                                  item.key?.toString() || ''
                                                )
                                              }
                                              validations={[
                                                Rules.Required,
                                                () =>
                                                  validateScheduleEndTime(
                                                    schedule,
                                                    businessStartTime,
                                                    businessEndTime
                                                  ) || '',
                                              ]}
                                            />
                                            <SelectBoxFormat
                                              label="作業予定テンプレート"
                                              value={schedule.templateId?.toString() ?? ''}
                                              displayValue={getTemplateDisplayValue(schedule.templateId)}
                                              items={templateSelectOptions}
                                              onChange={item =>
                                                handleTemplateChange(
                                                  convertedDayIndex,
                                                  scheduleIndex,
                                                  item.key ? Number(item.key) : null
                                                )
                                              }
                                              validations={[
                                                () => validateScheduleTemplate(schedule, templateIds) || '',
                                              ]}
                                            />
                                          </Col>
                                          <Col md={3} className="d-flex justify-content-end align-items-center">
                                            {isLast && dayShift.schedules.length < MAX_SHIFTS_PER_DAY && (
                                              <CustomButton
                                                outline
                                                className="me-2"
                                                icon="plus"
                                                onClick={() => handleAddSchedule(convertedDayIndex)}
                                              >
                                                追加
                                              </CustomButton>
                                            )}
                                            <Button
                                              outline
                                              color="danger"
                                              onClick={() => handleRemoveSchedule(convertedDayIndex, scheduleIndex)}
                                            >
                                              削除
                                            </Button>
                                          </Col>
                                        </Row>
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </CardBody>
                  </div>
                  <CardSubmitFooter
                    onCancel={handleCancel}
                    onSubmit={handleSubmit}
                    cancelDisabled={unchanged}
                    submitDisabled={!canSubmit}
                    updatedBy={worker?.updatedByName}
                    updatedAt={worker?.updatedAt}
                    submitName="default-shifts-submit"
                  />
                </>
              ) : (
                <CardBody className="d-flex align-items-center justify-content-center">
                  <div className="text-center">
                    <img className={`mx-auto d-block ${styles.placeholderImage}`} src={placeholder} alt="" />
                    <div className="font-middle fw-bold py-4">メンバーが選択されていません</div>
                    <div>メンバーを選択して、デフォルトシフトを編集しましょう。</div>
                  </div>
                </CardBody>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </NavMenu>
  )
}

export default DefaultShifts
